import React, { useEffect, useMemo, useState } from "react"
import type { IconProp } from "@fortawesome/fontawesome-svg-core"
import {
  faClose,
  faGlassWater,
  faLayerGroup,
  faPlus,
  faSliders,
  faTrashCan,
  faUtensils,
} from "@fortawesome/free-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { useTranslation } from "react-i18next"
import Animate, { AnimationTypes } from "../Animate/Animate"
import Button, { ButtonIcon } from "../Button/Button"
import { CountElements } from "../CountElements/CountElements"
import { GenericDropDown } from "../Dropdown/Dropdown"
import { PopupHelpButton } from "../Help/PopupHelpButton"
import { ListSelections } from "../ListSelections/ListSelections"
import ModalPortal from "../Modal/ModalPortal"
import { TitlePageResponsiveIconSmall } from "../Titles/Titles"
import { SIZE } from "../../configuration/Sizes"
import THEME from "../../configuration/Theme"
import { useGetCombo } from "../../hooks/Combo/useGetCombo"
import { useGetDrinks } from "../../hooks/Drinks/useGetDrinks"
import { useGetMeals } from "../../hooks/Meals/useGetMeals"
import { comboMemory, drinksMemory, mealsMemory } from "../../Objects/Memory/Memory"
import defaultStyle from "../../styles/defaultPageStyle.module.css"
import GetSpace from "../../utils/space"

export interface ProductAssociationRequest {
  targetType: number
  mealIds: number[]
  drinkIds: number[]
  comboIds: number[]
}

export interface ProductAssociationTargetValues {
  allProducts: number
  selectedProducts: number
  allMeals: number
  selectedMeals: number
  allDrinks: number
  selectedDrinks: number
  allCombos: number
  selectedCombos: number
}

export interface ProductAssociationLabels {
  targetLegend: string
  allProducts: string
  selectedProducts: string
  allMeals: string
  selectedMeals: string
  allDrinks: string
  selectedDrinks: string
  allCombos: string
  selectedCombos: string
  selectMealsAction: string
  selectDrinksAction: string
  selectCombosAction: string
  selectedMealsCount: string
  selectedDrinksCount: string
  selectedCombosCount: string
  selectedMealsTitle: string
  selectedDrinksTitle: string
  selectedCombosTitle: string
  noSelectedMeals: string
  noSelectedDrinks: string
  noSelectedCombos: string
}

export interface ProductAssociationInteractionKeys {
  closeDescriptionKey: string
  targetDescriptionKey: string
  confirmDescriptionKey: string
  addMealDescriptionKey: string
  removeMealDescriptionKey: string
  addDrinkDescriptionKey: string
  removeDrinkDescriptionKey: string
  addComboDescriptionKey: string
  removeComboDescriptionKey: string
}

interface ProductAssociationApplyPopupProps {
  title: string
  actionLabel: string
  helpTitle: string
  helpDescription: string
  helpBullets: string[]
  helpKey: string
  labels: ProductAssociationLabels
  targetValues: ProductAssociationTargetValues
  interactionKeys: ProductAssociationInteractionKeys
  onClose: () => void
  onConfirm: (request: ProductAssociationRequest) => Promise<void>
}

type OptionValue =
  | "all_products"
  | "selected_products"
  | "all_meals"
  | "selected_meals"
  | "all_drinks"
  | "selected_drinks"
  | "all_combos"
  | "selected_combos"

export const ProductAssociationApplyPopup: React.FC<ProductAssociationApplyPopupProps> = ({
  title,
  actionLabel,
  helpTitle,
  helpDescription,
  helpBullets,
  helpKey,
  labels,
  targetValues,
  interactionKeys,
  onClose,
  onConfirm,
}) => {
  const { t } = useTranslation(["buttons", "list"])
  
  const { handleGetMeals } = useGetMeals()
  
  const { handleGetDrinks } = useGetDrinks()
  
  const { handleGetCombo } = useGetCombo()

  const [targetType, setTargetType] = useState<OptionValue>("all_products")
  
  const [selectedMealIds, setSelectedMealIds] = useState<number[]>([])
  
  const [selectedDrinkIds, setSelectedDrinkIds] = useState<number[]>([])
  
  const [selectedComboIds, setSelectedComboIds] = useState<number[]>([])
  
  const [enableMealsSelection, setEnableMealsSelection] = useState(false)
  
  const [enableDrinksSelection, setEnableDrinksSelection] = useState(false)
  
  const [enableCombosSelection, setEnableCombosSelection] = useState(false)

  useEffect(() => {
    handleGetMeals(() => {})
    
    handleGetDrinks(() => {})
   
    handleGetCombo(() => {})
  }, [])

  const options = useMemo(
    () => [
      { label: labels.allProducts, value: "all_products" },
      { label: labels.selectedProducts, value: "selected_products" },
      { label: labels.allMeals, value: "all_meals" },
      { label: labels.selectedMeals, value: "selected_meals" },
      { label: labels.allDrinks, value: "all_drinks" },
      { label: labels.selectedDrinks, value: "selected_drinks" },
      { label: labels.allCombos, value: "all_combos" },
      { label: labels.selectedCombos, value: "selected_combos" },
    ],
    [labels],
  )

  const handleChangeTarget = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const value = event.target.value as OptionValue
    setTargetType(value)

    if (!(["selected_products", "selected_meals"] as OptionValue[]).includes(value)) {
      setSelectedMealIds([])
    }

    if (!(["selected_products", "selected_drinks"] as OptionValue[]).includes(value)) {
      setSelectedDrinkIds([])
    }

    if (!(["selected_products", "selected_combos"] as OptionValue[]).includes(value)) {
      setSelectedComboIds([])
    }
  }

  const getRequest = (): ProductAssociationRequest => {
    switch (targetType) {
      case "selected_products":
        return {
          targetType: targetValues.selectedProducts,
          mealIds: selectedMealIds,
          drinkIds: selectedDrinkIds,
          comboIds: selectedComboIds,
        }
      case "all_meals":
        return { targetType: targetValues.allMeals, mealIds: [], drinkIds: [], comboIds: [] }
      case "selected_meals":
        return { targetType: targetValues.selectedMeals, mealIds: selectedMealIds, drinkIds: [], comboIds: [] }
      case "all_drinks":
        return { targetType: targetValues.allDrinks, mealIds: [], drinkIds: [], comboIds: [] }
      case "selected_drinks":
        return { targetType: targetValues.selectedDrinks, mealIds: [], drinkIds: selectedDrinkIds, comboIds: [] }
      case "all_combos":
        return { targetType: targetValues.allCombos, mealIds: [], drinkIds: [], comboIds: [] }
      case "selected_combos":
        return { targetType: targetValues.selectedCombos, mealIds: [], drinkIds: [], comboIds: selectedComboIds }
      default:
        return { targetType: targetValues.allProducts, mealIds: [], drinkIds: [], comboIds: [] }
    }
  }

  const shouldSelectMeals = targetType === "selected_products" || targetType === "selected_meals"
  
  const shouldSelectDrinks = targetType === "selected_products" || targetType === "selected_drinks"
  
  const shouldSelectCombos = targetType === "selected_products" || targetType === "selected_combos"

  return (
    <ModalPortal>
      <div className={defaultStyle.overlay}>
        <Animate type={AnimationTypes.slideUpIn} duration="0.3s">
          <div className={defaultStyle.alert}>
            <TitlePageResponsiveIconSmall
              title={title}
              icon={faSliders}
              iconColor={THEME.colors.iconBGColor}
              children={
                <div className="flex items-center gap-1">
                  <PopupHelpButton
                    helpKey={helpKey}
                    title={helpTitle}
                    variant="global"
                    description={helpDescription}
                    bullets={helpBullets}
                  />

                  <ButtonIcon
                    sizeNumber={SIZE.buttonCloseIconSize}
                    bgColor={THEME.colors.buttonBGCloseColor}
                    iconColor={THEME.colors.buttonFGCloseColor}
                    icon={faClose}
                    onClick={onClose}
                    size="small"
                    descriptionKey={interactionKeys.closeDescriptionKey}
                  />
                </div>
              }
            />

            <GetSpace top={10} />

            <div className="p-2">
              <div className="rounded-lg border border-orange-100 bg-orange-50/40 p-3">
                <GenericDropDown
                  legend={labels.targetLegend}
                  data={options}
                  initialState={targetType}
                  handlerFuncion={handleChangeTarget}
                  descriptionKey={interactionKeys.targetDescriptionKey}
                />
              </div>

              {shouldSelectMeals && (
                <SelectionSection
                  title={labels.selectMealsAction}
                  countTitle={labels.selectedMealsCount}
                  emptyLabel={labels.noSelectedMeals}
                  selectedIds={selectedMealIds}
                  icon={faUtensils}
                  onAdd={() => setEnableMealsSelection(true)}
                  onRemove={(id) => setSelectedMealIds((current) => current.filter((itemId) => itemId !== id))}
                  addDescriptionKey={interactionKeys.addMealDescriptionKey}
                  removeDescriptionKey={interactionKeys.removeMealDescriptionKey}
                  getLabel={(id) => mealsMemory.findById(id)?.genericFoodEntity.name ?? "-"}
                />
              )}

              {shouldSelectDrinks && (
                <SelectionSection
                  title={labels.selectDrinksAction}
                  countTitle={labels.selectedDrinksCount}
                  emptyLabel={labels.noSelectedDrinks}
                  selectedIds={selectedDrinkIds}
                  icon={faGlassWater}
                  onAdd={() => setEnableDrinksSelection(true)}
                  onRemove={(id) => setSelectedDrinkIds((current) => current.filter((itemId) => itemId !== id))}
                  addDescriptionKey={interactionKeys.addDrinkDescriptionKey}
                  removeDescriptionKey={interactionKeys.removeDrinkDescriptionKey}
                  getLabel={(id) => drinksMemory.findById(id)?.genericFoodEntity.name ?? "-"}
                />
              )}

              {shouldSelectCombos && (
                <SelectionSection
                  title={labels.selectCombosAction}
                  countTitle={labels.selectedCombosCount}
                  emptyLabel={labels.noSelectedCombos}
                  selectedIds={selectedComboIds}
                  icon={faLayerGroup}
                  onAdd={() => setEnableCombosSelection(true)}
                  onRemove={(id) => setSelectedComboIds((current) => current.filter((itemId) => itemId !== id))}
                  addDescriptionKey={interactionKeys.addComboDescriptionKey}
                  removeDescriptionKey={interactionKeys.removeComboDescriptionKey}
                  getLabel={(id) => comboMemory.findById(id)?.genericFoodEntity.name ?? "-"}
                />
              )}
            </div>

            <div className={defaultStyle.divisionBar}></div>

            <div className={defaultStyle.buttonsController}>
              <Button
                label={actionLabel}
                typeButton="button"
                type="primary"
                size="medium"
                bgColor={THEME.colors.buttonBGSaveColor}
                onClick={() => onConfirm(getRequest())}
                descriptionKey={interactionKeys.confirmDescriptionKey}
              />
            </div>
          </div>
        </Animate>
      </div>

      {enableMealsSelection && (
        <ListSelections
          title={labels.selectedMealsTitle}
          icon={faUtensils}
          listCards={mealsMemory.getListCards(true)}
          messageCount={t("list:label_meals_page_message_count")}
          messageEmpty={t("list:label_meals_page_message_empty")}
          onClinck={(id) => {
            setSelectedMealIds((current) => (current.includes(id) ? current : [...current, id]))
            setEnableMealsSelection(false)
          }}
          onClonse={() => setEnableMealsSelection(false)}
        />
      )}

      {enableDrinksSelection && (
        <ListSelections
          title={labels.selectedDrinksTitle}
          icon={faGlassWater}
          listCards={drinksMemory.getListCards(true)}
          messageCount={t("list:label_drinks_page_message_count")}
          messageEmpty={t("list:label_drinks_page_message_empty")}
          onClinck={(id) => {
            setSelectedDrinkIds((current) => (current.includes(id) ? current : [...current, id]))
            setEnableDrinksSelection(false)
          }}
          onClonse={() => setEnableDrinksSelection(false)}
        />
      )}

      {enableCombosSelection && (
        <ListSelections
          title={labels.selectedCombosTitle}
          icon={faLayerGroup}
          listCards={comboMemory.getListCards(true)}
          messageCount={t("list:label_combo_page_message_count")}
          messageEmpty={t("list:label_combo_page_message_empty")}
          onClinck={(id) => {
            setSelectedComboIds((current) => (current.includes(id) ? current : [...current, id]))
            setEnableCombosSelection(false)
          }}
          onClonse={() => setEnableCombosSelection(false)}
        />
      )}
    </ModalPortal>
  )
}

interface SelectionSectionProps {
  title: string
  countTitle: string
  emptyLabel: string
  selectedIds: number[]
  icon: IconProp
  onAdd: () => void
  onRemove: (id: number) => void
  addDescriptionKey: string
  removeDescriptionKey: string
  getLabel: (id: number) => string
}

const SelectionSection: React.FC<SelectionSectionProps> = ({
  title,
  countTitle,
  emptyLabel,
  selectedIds,
  icon,
  onAdd,
  onRemove,
  addDescriptionKey,
  removeDescriptionKey,
  getLabel,
}) => {
  const { t } = useTranslation("buttons")

  return (
    <>
      <GetSpace top={10} />

      <div className="rounded-lg border border-slate-200 bg-slate-50/70 p-3">
        <div className="flex items-center justify-between gap-2">
          <div className="flex min-w-0 items-center gap-2">
            <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-orange-100 text-orange-600">
              <FontAwesomeIcon icon={icon} />
            </span>

            <span className="min-w-0 break-words text-sm font-semibold text-slate-700">{title}</span>
          </div>

          <ButtonIcon
            typeButton="button"
            type="primary"
            size="medium"
            icon={faPlus}
            sizeNumber={SIZE.buttonCloseIconSize}
            bgColor={THEME.colors.buttonBGPlusColor}
            iconColor={THEME.colors.buttonFGPlusColor}
            onClick={onAdd}
            descriptionKey={addDescriptionKey}
          />
        </div>

        <GetSpace top={5} />
        
        <CountElements count={selectedIds.length} title={countTitle} />
        
        <GetSpace top={5} />

        {selectedIds.length === 0 && <span className="text-xs text-slate-500">{emptyLabel}</span>}

        {selectedIds.map((id) => (
          <Animate key={id} type={AnimationTypes.fadeInTraslate} duration="1.2s">
            <div className="mt-2 flex items-center justify-between gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2">
              <span className="min-w-0 flex-1 truncate text-sm font-medium text-slate-700" title={getLabel(id)}>
                {getLabel(id)}
              </span>

              <button
                className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-red-50 transition-colors hover:bg-red-100"
                type="button"
                onClick={() => onRemove(id)}
                title={t("button_remove")}
                data-interaction-description-key={removeDescriptionKey}
              >
                <FontAwesomeIcon icon={faTrashCan} color={THEME.colors.buttonBGRemoveColor} />
              </button>
            </div>
          </Animate>
        ))}
      </div>
    </>
  )
}
